import React, { useContext } from 'react';
import classNames from 'classnames';
import { CopyToClipboard } from 'react-copy-to-clipboard';

import {
  useTranslation,
  toastify,
  Icon,
  PopoverTooltip,
} from '@testsigma/atoms';
import { UserPreferenceContext } from '@contexts/user-preference-context';
import { useSFConnectionState } from '@contexts/sf-connection-context';
import { getIsGlobalConnection } from '@modules/salesforce/utils/utils';

interface RowProps {
  label: string;
  value: string;
}

/**
 * @returns 	Fields that display the project and version names and serve as the trigger point for the tooltip.
 */
const TriggerPoint = () => {  
  const isGlobalConnection = getIsGlobalConnection();
  //  Retrieve user preferences to obtain projectName and applicationVersionName.
  const { userPreference } = useContext(UserPreferenceContext);
  const { selectedApplication, selectedProject } = useSFConnectionState();
  const projectName = isGlobalConnection ? selectedProject?.name :
    userPreference?.preference?.projectPreference?.selectedProject?.name;
  const applicationName = isGlobalConnection ? selectedApplication?.name :
    userPreference?.preference?.projectPreference?.selectedApplication?.name;

  return (
    <div
      className={classNames(
        'truncate text-small leading-5 text-secondary-unique-600',
        '!w-[320px]',
      )}>{`${projectName}/${applicationName}`}</div>
  );
};

/**
 * @returns Renders a row for tooltip content.
 */
const TooltipRow = (props: RowProps) => {
  const { label, value } = props;
  const { t } = useTranslation();

  return (
    <div className={classNames('flex flex-row gap-x-2 justify-center')}>
      <div>{label}</div>
      <div className='truncate text-xs'>{`${value}`}</div>
      <CopyToClipboard
        text={`${value}`}
        onCopy={() => {
          toastify(t('common.copy_success'), { type: 'success' });
        }}>
        <div className='text-secondary-100 ml-auto font-medium flex gap-1 items-center cursor-pointer'>
          <Icon name={'clone'} className={'font-medium'} /> {t('common.copy')}
        </div>
      </CopyToClipboard>
    </div>
  );
};

/**
 * @returns Renders the view for display in the tooltip pop-over.
 */
const TooltipView = () => {

  const { selectedApplication, selectedProject } = useSFConnectionState();
  const isGlobalConnection = getIsGlobalConnection();

  //  Retrieve user preferences to obtain projectName and applicationVersionName.
  const { userPreference } = useContext(UserPreferenceContext);

  const projectName = isGlobalConnection? selectedProject?.name :
    userPreference?.preference?.projectPreference?.selectedProject?.name;
  const applicationName = isGlobalConnection? selectedApplication?.name :
    userPreference?.preference?.projectPreference?.selectedApplication?.name;

  return (
    <div>
      <TooltipRow label='Project:' value={projectName} />
      <TooltipRow label='Application:' value={applicationName} />
    </div>
  );
};

/**
 * Renders the UI for the Project/Version fields with a tooltip that allows users to copy values.
 *
 * @returns UI for fields displaying project/version values with an actionable tooltip for viewing and copying these values
 */
const ProjectVersionFields: React.FC = () => {
  //  Retrieve user preferences to obtain projectName and applicationVersionName.
  const { userPreference } = useContext(UserPreferenceContext);
  const { selectedApplication, selectedProject } = useSFConnectionState();
  const isGlobalConnection = getIsGlobalConnection();

  const projectName = isGlobalConnection ? selectedProject?.name :
    userPreference?.preference?.projectPreference?.selectedProject?.name;

  const applicationName = isGlobalConnection ? selectedApplication?.name :
    userPreference?.preference?.projectPreference?.selectedVersion?.name;

  if (!projectName) {
    return null;
  }

  if (!applicationName  ) {
    return null;
  }

  return (
    <PopoverTooltip
      triggerPoint={<TriggerPoint />}
      tooltipView={<TooltipView />}
    />
  );
};

export { ProjectVersionFields };
